// Avisos: faixa de novidades, sino de notificações e acesso ao banco desses avisos.
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect } from "react";
import React from "react";
import ReactDOM from "react-dom";
import { sb } from "../lib/supabase";
import { hexToRgb, useTheme } from "../lib/theme";
import { NavIcon } from "./icons";
import { RichText, SheetModal } from "./ui";

async function dbGetNotifications(userId, userCreatedAt) {
  try {
    // Broadcasts só aparecem se foram enviadas DEPOIS que o usuário se cadastrou
    const filter = userCreatedAt
      ? `target_user_id.eq.${userId},and(target_user_id.is.null,created_at.gte.${userCreatedAt})`
      : `target_user_id.is.null,target_user_id.eq.${userId}`;
    const { data } = await sb.from("notifications")
      .select("*")
      .or(filter)
      .order("created_at", { ascending: false })
      .limit(50);
    const list = data || [];
    // Avisos gerais: a leitura é de cada pessoa (tabela notification_reads)
    let readIds = new Set();
    try {
      const { data: reads } = await sb.from("notification_reads").select("notification_id").eq("user_id", userId);
      readIds = new Set((reads || []).map(x => x.notification_id));
    } catch {}
    return list.map(n => n.target_user_id ? n : { ...n, is_read: readIds.has(n.id) });
  } catch { return []; }
}

async function dbMarkNotifRead(id, userId, isBroadcast) {
  try {
    if (isBroadcast) await sb.from("notification_reads").upsert({ user_id: userId, notification_id: id }, { onConflict: "user_id,notification_id", ignoreDuplicates: true });
    else await sb.from("notifications").update({ is_read: true }).eq("id", id);
  } catch {}
}

async function dbMarkAllNotifsRead(userId, notifs) {
  try {
    const bc = (notifs || []).filter(n => !n.target_user_id && !n.is_read).map(n => ({ user_id: userId, notification_id: n.id }));
    if (bc.length) await sb.from("notification_reads").upsert(bc, { onConflict: "user_id,notification_id", ignoreDuplicates: true });
    await sb.from("notifications").update({ is_read: true }).eq("target_user_id", userId).eq("is_read", false);
  } catch {}
}

const isNewsNotif = n => !!n && /^novidades/i.test(String(n.title || "").trim());

const newsPreview = n => String(n.body || "").split("\n").map(x => x.trim()).find(x => x && !/^##/.test(x) && !/^[-•]/.test(x)) || "";

function NewsModal({ n, onClose }) {
  const t = useTheme();
  return (
    <SheetModal labelledBy="g17-news" onClose={onClose} maxWidth={640}
      title={n.title}
      subtitle={new Date(n.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
      footer={<><span /><button onClick={onClose} style={{ background: t.ac, color: t.mode === "dark" ? "#0a0f0b" : "#fff", border: "none", borderRadius: 10, padding: "0.7rem 1.4rem", fontWeight: 800, cursor: "pointer", fontSize: "0.9rem" }}>Entendi</button></>}>
      <RichText text={n.body} t={t} />
    </SheetModal>
  );
}

// Faixa no Início para avisos de "Novidades" ainda não lidos
export function NewsBanner({ userId, userCreatedAt }) {
  const t = useTheme();
  const [news, setNews] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    dbGetNotifications(userId, userCreatedAt).then(list => {
      if (!alive) return;
      const n = list.find(x => isNewsNotif(x) && !x.is_read && Date.now() - new Date(x.created_at).getTime() < 30 * 86400000);
      setNews(n || null);
    });
    return () => { alive = false; };
  }, [userId, userCreatedAt]);
  if (!news) return null;
  const done = () => { dbMarkNotifRead(news.id, userId, !news.target_user_id).then(() => window.dispatchEvent(new Event("g17-notifs-changed"))); setOpen(false); setNews(null); };
  const preview = newsPreview(news) || "Veja tudo o que mudou no G17.";
  return (
    <>
      <div style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", gap: "0.9rem", flexWrap: "wrap", marginBottom: "1.2rem", padding: "1rem 1.1rem", borderRadius: 14,
        background: `linear-gradient(135deg, ${t.acDim} 0%, ${t.bgCard} 70%)`, border: `1px solid ${t.ac}` }}>
        <span style={{ width: 42, height: 42, borderRadius: 12, background: t.ac, color: t.mode === "dark" ? "#0a0f0b" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <NavIcon id="estrela" size={22} />
        </span>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div style={{ fontSize: "0.66rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: t.ac }}>Novidades</div>
          <div style={{ fontSize: "1rem", fontWeight: 800, color: t.text, lineHeight: 1.25 }}>{news.title}</div>
          <div style={{ fontSize: "0.8rem", color: t.textDim, marginTop: 2, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{preview}</div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button onClick={() => setOpen(true)} style={{ background: t.ac, color: t.mode === "dark" ? "#0a0f0b" : "#fff", border: "none", borderRadius: 10, padding: "0.6rem 1.1rem", fontWeight: 800, cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" }}>Ver novidades</button>
          <button onClick={done} aria-label="Dispensar" title="Dispensar" style={{ background: "transparent", border: `1px solid ${t.border}`, borderRadius: 10, width: 36, height: 36, color: t.textDim, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      </div>
      {open && <NewsModal n={news} onClose={done} />}
    </>
  );
}

// ── NOTIFICATION BELL ─────────────────────────────────────────────────────────
// ── NOTIF CARD — estilo post, imagem em destaque ────────────────────────────
function NotifCard({ n, t, rv, gv, bv, onRead, onOpenNews, timeAgo }) {
  const [expanded, setExpanded] = React.useState(false);

  function handleClick() {
    if (isNewsNotif(n) && onOpenNews) { onOpenNews(); return; }
    onRead();
    setExpanded(p => !p);
  }
  if (isNewsNotif(n)) {
    const preview = newsPreview(n);
    const heads = String(n.body || "").split("\n").map(x => x.trim()).filter(x => /^##\s+/.test(x)).map(x => x.replace(/^##\s+/, "").replace(/\*\*/g, ""));
    return (
      <div onClick={handleClick} role="button" tabIndex={0} onKeyDown={e => { if (e.key === "Enter") handleClick(); }}
        style={{ margin:"0.5rem 0.75rem", borderRadius:14, overflow:"hidden", cursor:"pointer", border:`1.5px solid ${n.is_read ? `rgba(${rv},${gv},${bv},0.35)` : t.ac}`,
          background:`linear-gradient(160deg, rgba(${rv},${gv},${bv},0.16) 0%, rgba(${rv},${gv},${bv},0.04) 60%)`,
          boxShadow: n.is_read ? "none" : `0 4px 22px rgba(${rv},${gv},${bv},0.22)` }}>
        <div style={{ padding:"0.95rem 1rem" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:"0.5rem", marginBottom:"0.5rem" }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:"0.35rem", fontSize:"0.64rem", fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", color: t.mode === "dark" ? "#0a0f0b" : "#fff", background:t.ac, padding:"0.2rem 0.55rem", borderRadius:20 }}>
              <NavIcon id="estrela" size={11} strokeWidth={2.4} />Novidades
            </span>
            <span style={{ display:"flex", alignItems:"center", gap:"0.4rem", fontSize:"0.65rem", color:t.textFaint }}>{timeAgo(n.created_at)}{!n.is_read && <span style={{ width:7, height:7, borderRadius:"50%", background:t.ac, boxShadow:`0 0 6px ${t.ac}` }} />}</span>
          </div>
          <div style={{ fontSize:"1rem", fontWeight:800, color:t.text, lineHeight:1.3, marginBottom:"0.3rem" }}>{n.title}</div>
          {preview && <div style={{ fontSize:"0.8rem", color:t.textDim, lineHeight:1.5, marginBottom:"0.55rem" }}>{preview}</div>}
          {heads.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:"0.3rem", marginBottom:"0.65rem" }}>
              {heads.slice(0, 6).map(h => <span key={h} style={{ fontSize:"0.68rem", color:t.textMid, background:t.bgInner, border:`1px solid ${t.borderIn}`, borderRadius:20, padding:"0.15rem 0.55rem" }}>{h}</span>)}
              {heads.length > 6 && <span style={{ fontSize:"0.68rem", color:t.textFaint, padding:"0.15rem 0.3rem" }}>+{heads.length - 6}</span>}
            </div>
          )}
          <span style={{ display:"inline-flex", alignItems:"center", gap:"0.3rem", fontSize:"0.8rem", fontWeight:700, color:t.ac }}>Ver todas as novidades
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div onClick={handleClick}
      style={{
        margin:"0.5rem 0.75rem",
        borderRadius:14,
        // Não lidas: borda cheia na cor de acento, bem visível.
        // Lidas: borda quase invisível (some assim que o usuário abre a notificação).
        border: n.is_read ? `1px solid rgba(${rv},${gv},${bv},0.08)` : `1.5px solid ${t.ac}`,
        background: n.is_read ? `rgba(${rv},${gv},${bv},0.03)` : `rgba(${rv},${gv},${bv},0.07)`,
        overflow:"hidden",
        cursor:"pointer",
        transition:"all 0.18s",
        boxShadow: n.is_read ? "none" : `0 0 0 1px rgba(${rv},${gv},${bv},0.12), 0 2px 16px rgba(${rv},${gv},${bv},0.15)`,
      }}
      onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.1)`; if(n.is_read) e.currentTarget.style.borderColor=`rgba(${rv},${gv},${bv},0.25)`;}}
      onMouseLeave={e=>{e.currentTarget.style.background=n.is_read?`rgba(${rv},${gv},${bv},0.03)`:`rgba(${rv},${gv},${bv},0.07)`; if(n.is_read) e.currentTarget.style.borderColor=`rgba(${rv},${gv},${bv},0.08)`;}}
    >
      {/* Imagens */}
      {(() => {
        const imgs = Array.isArray(n.image_urls) && n.image_urls.length ? n.image_urls : (n.image_url ? [n.image_url] : []);
        if (!imgs.length) return null;
        if (imgs.length === 1) return (
          <div style={{ width:"100%", maxHeight: expanded ? 320 : 180, overflow:"hidden", transition:"max-height 0.3s ease" }}>
            <img src={imgs[0]} alt="" style={{ width:"100%", objectFit:"cover", display:"block" }} />
          </div>
        );
        return (
          <div style={{ display:"flex", gap:2, overflowX:"auto", maxHeight:expanded?320:180, scrollbarWidth:"none" }}>
            {imgs.map((src,i) => (
              <img key={i} src={src} alt="" style={{ flex:"0 0 auto", width: imgs.length===2?"50%":"33.3%", objectFit:"cover", display:"block", maxHeight:expanded?320:180 }} />
            ))}
          </div>
        );
      })()}

      {/* Conteúdo */}
      <div style={{ padding:"0.9rem 1rem" }}>
        {/* System sender badge */}
        <div style={{ display:"flex", alignItems:"center", gap:"0.4rem", marginBottom:"0.45rem" }}>
          <div style={{ width:20, height:20, borderRadius:"50%", background:`rgba(${rv},${gv},${bv},0.15)`, border:`1px solid rgba(${rv},${gv},${bv},0.3)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.65rem" }}>⬡</div>
          <span style={{ fontSize:"0.68rem", fontWeight:700, color:`rgba(${rv},${gv},${bv},0.9)`, letterSpacing:"0.06em", textTransform:"uppercase" }}>G17 App</span>
          <span style={{ fontSize:"0.62rem", color:t.textFaint }}>· Notificação do sistema</span>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"0.5rem", marginBottom:"0.35rem" }}>
          <span style={{ fontSize:"0.88rem", fontWeight:800, color: n.is_read ? t.textMid : t.text, lineHeight:1.3, flex:1 }}>{n.title}</span>
          <div style={{ display:"flex", alignItems:"center", gap:"0.4rem", flexShrink:0 }}>
            <span style={{ fontSize:"0.65rem", color:t.textFaint }}>{timeAgo(n.created_at)}</span>
            {!n.is_read && <div style={{ width:7, height:7, borderRadius:"50%", background:t.ac, boxShadow:`0 0 6px ${t.ac}` }} />}
          </div>
        </div>
        <p style={{ margin:0, fontSize:"0.8rem", color:t.textDim, lineHeight:1.55, whiteSpace:"pre-line",
          overflow: expanded ? "visible" : "hidden",
          display: expanded ? "block" : "-webkit-box",
          WebkitLineClamp: expanded ? "unset" : 3,
          WebkitBoxOrient: "vertical",
        }}>{n.body}</p>
        {n.body && n.body.length > 120 && (
          <span style={{ fontSize:"0.72rem", color:t.ac, fontWeight:600, marginTop:"0.3rem", display:"block" }}>
            {expanded ? "Ver menos ↑" : "Ver mais ↓"}
          </span>
        )}
      </div>
    </div>
  );
}

function BellIcon({ size=16, color="currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

export function NotificationBell({ userId, userCreatedAt }) {
  const t = useTheme();
  const [open, setOpen] = React.useState(false);
  const [notifs, setNotifs] = React.useState([]);
  const [rv, gv, bv] = hexToRgb(t.ac);

  React.useEffect(() => {
    if (!userId) return;
    loadNotifs();
    const interval = setInterval(loadNotifs, 30000);
    window.addEventListener("g17-notifs-changed", loadNotifs);
    return () => { clearInterval(interval); window.removeEventListener("g17-notifs-changed", loadNotifs); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [userId]);

  async function loadNotifs() {
    const data = await dbGetNotifications(userId, userCreatedAt);
    setNotifs(data);
  }

  const unread = notifs.filter(n => !n.is_read).length;

  const [newsOpen, setNewsOpen] = React.useState(null);
  async function markRead(id) {
    const n0 = notifs.find(x => x.id === id);
    setNotifs(p => p.map(n => n.id === id ? { ...n, is_read: true } : n));
    await dbMarkNotifRead(id, userId, n0 && !n0.target_user_id);
  }

  async function markAllRead() {
    const cur = notifs;
    setNotifs(p => p.map(n => ({ ...n, is_read: true })));
    await dbMarkAllNotifsRead(userId, cur);
  }

  function timeAgo(ts) {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "agora";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h/24)}d`;
  }

  return (
    <div style={{ position:"relative" }}>
      <button onClick={() => setOpen(p => !p)} aria-label="Notificações"
        style={{ background:"transparent", border:"none", borderRadius:7, width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative", color: open ? t.ac : t.textFaint, transition:"color 0.18s", flexShrink:0, padding:0 }}
        onMouseEnter={e=>e.currentTarget.style.color=t.ac}
        onMouseLeave={e=>{ if (!open) e.currentTarget.style.color=t.textFaint; }}>
        <BellIcon size={15} color="currentColor" />
        {unread > 0 && (
          <span style={{ position:"absolute", top:-3, right:-3, minWidth:14, height:14, borderRadius:99, background:t.ac, color: t.mode==="dark" ? "#000" : "#fff", fontSize:"0.55rem", fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 2px", boxShadow:`0 0 6px ${t.ac}`, lineHeight:1 }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <style>{`
            @keyframes notif-slide-in {
              from { transform: translateX(-100%); opacity: 0; }
              to   { transform: translateX(0);     opacity: 1; }
            }
            @keyframes notif-overlay-in {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>
          {/* Overlay — cobre só a área à direita da sidebar */}
          <div style={{ position:"fixed", inset:0, zIndex:9990, background:"rgba(0,0,0,0.65)", backdropFilter:"blur(5px)", animation:"notif-overlay-in 0.2s ease" }} onClick={() => setOpen(false)} />

          {/* Painel — slide da esquerda extrema, segue tema */}
          <div style={{
            position:"fixed", top:0, left:0, bottom:0,
            width:"min(400px, 100vw)",
            display:"flex", flexDirection:"column",
            background: t.mode==="dark"
              ? `rgba(${Math.max(8,Math.round(rv*0.06))},${Math.max(13,Math.round(gv*0.08))},${Math.max(20,Math.round(bv*0.11))},0.97)`
              : t.bgCard,
            borderRight:`1px solid rgba(${rv},${gv},${bv},0.22)`,
            boxShadow:`32px 0 80px rgba(0,0,0,0.55), inset -1px 0 0 rgba(${rv},${gv},${bv},0.08)`,
            backdropFilter:"blur(32px)",
            zIndex:9991,
            animation:"notif-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          }}>
            {/* Header */}
            <div style={{ padding:"1.4rem 1.4rem 1rem", borderBottom:`1px solid rgba(${rv},${gv},${bv},0.1)`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:"0.6rem" }}>
                <BellIcon size={16} color={t.ac} />
                <span style={{ fontSize:"1rem", fontWeight:800, color:t.text }}>
                  Notificações
                </span>
                {unread > 0 && (
                  <span style={{ background:`rgba(${rv},${gv},${bv},0.15)`, color:t.ac, fontSize:"0.7rem", fontWeight:700, padding:"0.15rem 0.5rem", borderRadius:99, border:`1px solid rgba(${rv},${gv},${bv},0.25)` }}>
                    {unread} nova{unread>1?"s":""}
                  </span>
                )}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:"0.75rem" }}>
                {unread > 0 && (
                  <button onClick={markAllRead} style={{ background:"transparent", border:"none", color:t.ac, fontSize:"0.75rem", cursor:"pointer", fontWeight:600, padding:0 }}>
                    Marcar lidas
                  </button>
                )}
                <button onClick={() => setOpen(false)}
                  style={{ background:`rgba(${rv},${gv},${bv},0.08)`, border:`1px solid rgba(${rv},${gv},${bv},0.15)`, color:t.textDim, cursor:"pointer", width:28, height:28, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.85rem", transition:"all 0.15s" }}
                  onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.15)`;e.currentTarget.style.color=t.text;}}
                  onMouseLeave={e=>{e.currentTarget.style.background=`rgba(${rv},${gv},${bv},0.08)`;e.currentTarget.style.color=t.textDim;}}>
                  ✕
                </button>
              </div>
            </div>

            {/* Lista scrollável */}
            <div style={{ overflowY:"auto", flex:1, padding:"0.5rem 0" }}>
              {notifs.length === 0 ? (
                <div style={{ padding:"4rem 2rem", textAlign:"center", color:t.textFaint }}>
                  <BellIcon size={32} color={t.textFaint} />
                  <p style={{ margin:"1rem 0 0", fontSize:"0.85rem" }}>Nenhuma notificação</p>
                </div>
              ) : notifs.map(n => (
                <NotifCard key={n.id} n={n} t={t} rv={rv} gv={gv} bv={bv}
                  onRead={() => !n.is_read && markRead(n.id)}
                  onOpenNews={() => { if (!n.is_read) markRead(n.id); setNewsOpen(n); }}
                  timeAgo={timeAgo} />
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
      {newsOpen && <NewsModal n={newsOpen} onClose={() => setNewsOpen(null)} />}
    </div>
  );
}
